<template>
  <div class="card shadow-sm mt-4">
    <!-- Header (Clickable to collapse/expand) -->
    <div
      class="card-header bg-white d-flex justify-content-between align-items-center py-3 cursor-pointer"
      style="cursor: pointer"
      @click="isOpen = !isOpen"
    >
      <h6 class="m-0 font-weight-bold text-primary">
        Revenue by Client over selected period
      </h6>
      <button
        type="button"
        class="btn btn-sm btn-link text-decoration-none p-0"
      >
        <i
          class="fas"
          :class="isOpen ? 'fa-chevron-up' : 'fa-chevron-down'"
        ></i>
      </button>
    </div>

    <!-- Collapsible Body -->
    <div v-show="isOpen" class="card-body">
      <!-- Filter Inputs Row -->
      <div class="row align-items-end mb-4">
        <!-- Date From -->
        <div class="col-md-3 col-sm-6 mb-3 mb-md-0">
          <label class="small text-muted font-weight-bold mb-1">From</label>
          <input
            v-model="filters.startDate"
            type="date"
            class="form-control form-control-sm"
          />
        </div>

        <!-- Date To -->
        <div class="col-md-3 col-sm-6 mb-3 mb-md-0">
          <label class="small text-muted font-weight-bold mb-1">To</label>
          <input
            v-model="filters.endDate"
            type="date"
            class="form-control form-control-sm"
          />
        </div>

        <!-- Client MultiSelect Dropdown -->
        <div class="col-md-4 col-sm-6 mb-3 mb-md-0">
          <div class="multiselect-align-fix">
            <multi-select-dropdown
              v-model="filters.clients"
              :field="clientField"
              :margin-bottom="0"
            />
          </div>
        </div>

        <!-- Trigger Button -->
        <div class="col-md-2 col-sm-6">
          <button
            type="button"
            class="btn btn-primary btn-sm btn-block"
            :disabled="loading"
            @click="fetchRevenue"
          >
            <span
              v-if="loading"
              class="spinner-border spinner-border-sm mr-1"
            ></span>
            Display Revenue
          </button>
        </div>
      </div>

      <!-- Results Display Section -->
      <div v-if="loading" class="text-center py-4">
        <div
          class="spinner-border spinner-border-sm text-primary"
          role="status"
        ></div>
      </div>

      <div v-else-if="revenueData" class="mt-3">
        <!-- Total Revenue Summary Card & Toggle Button Row -->
        <div class="row align-items-center mb-3">
          <div class="col-md-4">
            <div class="p-3 bg-primary text-white rounded border text-center">
              <span class="d-block text-white-50 small font-weight-bold mb-1">
                TOTAL NET REVENUE
              </span>
              <h5 class="m-0 font-weight-bold">
                {{ formatCurrency(revenueData.total_net_amount) }}
              </h5>
            </div>
          </div>

          <div class="col-md-8 text-md-right mt-3 mt-md-0">
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm"
              @click="showDetails = !showDetails"
            >
              <i
                class="fas mr-1"
                :class="showDetails ? 'fa-eye-slash' : 'fa-eye'"
              ></i>
              {{
                showDetails
                  ? "Hide Detailed Invoices"
                  : "Show Detailed Invoices"
              }}
            </button>
          </div>
        </div>

        <!-- Detailed Breakdown Table Section -->
        <div v-if="showDetails" class="mt-4 border-top pt-3">
          <!-- Toolbar & Export Buttons -->
          <div
            class="d-flex justify-content-between align-items-center mb-3 flex-wrap"
          >
            <h6 class="font-weight-bold text-dark m-0">
              Invoice Records ({{ revenueRows.length }})
            </h6>

            <table-export-buttons
              v-if="revenueRows.length > 0"
              :columns="columnConfig"
              :data="revenueRows"
              file-name="revenue_by_client_report"
              title="Client Revenue Invoices Report"
            />
          </div>

          <!-- Data Table -->
          <div class="table-scroll-container border rounded">
            <table
              class="table table-sm table-hover table-striped revenue-table m-0"
            >
              <thead class="thead-light">
                <tr>
                  <th
                    v-for="col in columnConfig"
                    :key="col.key"
                    class="small text-nowrap"
                  >
                    {{ col.label }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="paginatedRows.length === 0">
                  <td
                    :colspan="columnConfig.length"
                    class="text-center text-muted py-3 small"
                  >
                    No rows to display.
                  </td>
                </tr>
                <tr
                  v-for="(row, idx) in paginatedRows"
                  :key="row.invoice_id || idx"
                >
                  <td
                    v-for="col in columnConfig"
                    :key="col.key"
                    class="small align-middle"
                    :title="
                      col.formatter
                        ? col.formatter(row[col.key], row)
                        : row[col.key]
                    "
                  >
                    {{
                      col.formatter
                        ? col.formatter(row[col.key], row)
                        : row[col.key] || "-"
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination Controls -->
          <div
            v-if="totalPages > 1"
            class="d-flex justify-content-between align-items-center mt-3"
          >
            <span class="small text-muted">
              Showing {{ pageStartItem }} to {{ pageEndItem }} of
              {{ revenueRows.length }} entries
            </span>

            <ul class="pagination pagination-sm m-0">
              <li
                class="page-item"
                :class="{ disabled: currentPage === 1 }"
                @click="currentPage > 1 && currentPage--"
              >
                <button class="page-link" type="button">Previous</button>
              </li>

              <li
                v-for="page in totalPages"
                :key="page"
                class="page-item"
                :class="{ active: currentPage === page }"
                @click="currentPage = page"
              >
                <button class="page-link" type="button">{{ page }}</button>
              </li>

              <li
                class="page-item"
                :class="{ disabled: currentPage === totalPages }"
                @click="currentPage < totalPages && currentPage++"
              >
                <button class="page-link" type="button">Next</button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Initial Empty State -->
      <div v-else-if="hasSearched" class="text-center py-3 text-muted small">
        No revenue records found for the selected criteria.
      </div>
    </div>
  </div>
</template>

<script>
import MultiSelectDropdown from "@/components/ui/MultiSelectDropdown.vue";
import TableExportButtons from "@/components/tableExport/TableExportButtons.vue";
import { getClientsRevenue, getClients } from "@/apis/clients";

export default {
  name: "ClientsRevenue",
  components: {
    MultiSelectDropdown,
    TableExportButtons,
  },
  data() {
    return {
      isOpen: false,
      loading: false,
      hasSearched: false,
      showDetails: false,
      currentPage: 1,
      itemsPerPage: 20,
      clientOptions: [],
      filters: {
        startDate: "",
        endDate: "",
        clients: [],
      },
      revenueData: null,
      columnConfig: [
        { label: "Client Name", key: "client_name" },
        { label: "Invoice Number", key: "invoice_number" },
        {
          label: "Invoice Date",
          key: "invoice_date",
          formatter: (val) => this.formatDate(val),
        },
        {
          label: "Net Amount",
          key: "net_amount",
          formatter: (val) => this.formatCurrency(val),
        },
        { label: "Project Number", key: "project_number" },
        { label: "Project Name", key: "project_name" },
        { label: "Project Status", key: "project_status" },
        { label: "Invoicing Status", key: "project_invoicing_status" },
        { label: "Company Number", key: "client_company_number" },
        { label: "VAT Number", key: "client_vat_number" },
      ],
    };
  },
  computed: {
    clientField() {
      return {
        name: "clients",
        label: "Client",
        placeholder: "Select clients...",
        options: this.clientOptions,
      };
    },

    revenueRows() {
      return this.revenueData?.invoices || [];
    },

    totalPages() {
      return Math.ceil(this.revenueRows.length / this.itemsPerPage) || 1;
    },

    paginatedRows() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.revenueRows.slice(start, end);
    },

    pageStartItem() {
      if (this.revenueRows.length === 0) return 0;
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },

    pageEndItem() {
      const end = this.currentPage * this.itemsPerPage;
      return end > this.revenueRows.length ? this.revenueRows.length : end;
    },
  },
  async mounted() {
    await this.fetchClientOptions();
  },
  methods: {
    async fetchClientOptions() {
      try {
        const response = await getClients();
        console.log("Clients resp:", response);
        const clientsList = response?.data || [];

        this.clientOptions = clientsList
          .map((client) => ({
            text: client.name,
            value: client.id,
          }))
          .sort((a, b) => (a.text || "").localeCompare(b.text || ""));

        console.log("clientsList", clientsList);
      } catch (error) {
        console.error("Failed to load clients list:", error);
      }
    },

    async fetchRevenue() {
      this.loading = true;
      this.hasSearched = true;
      this.currentPage = 1;

      try {
        const payload = {
          startDate: this.filters.startDate || null,
          endDate: this.filters.endDate || null,
        };

        if (this.filters.clients && this.filters.clients.length > 0) {
          payload.client_ids = this.filters.clients
            .map((item) => item.value)
            .join(",");
        }

        if (!payload.startDate) delete payload.startDate;
        if (!payload.endDate) delete payload.endDate;

        const response = await getClientsRevenue(payload);
        this.revenueData = response?.data || response;
      } catch (error) {
        console.error("Failed to fetch client revenue report:", error);
        this.revenueData = null;
      } finally {
        this.loading = false;
      }
    },

    formatCurrency(value) {
      const amount = Number(value) || 0;
      return new Intl.NumberFormat("en-IE", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
      }).format(amount);
    },

    formatDate(dateVal) {
      if (!dateVal) return "-";
      const date = new Date(dateVal);
      if (isNaN(date.getTime())) return dateVal;

      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.multiselect-align-fix {
  position: relative;
  top: 9px;
}

/* Enforce strict container width with horizontal scrolling */
.table-scroll-container {
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

/* Fix table stretching and handle wide cell content gracefully */
.revenue-table {
  width: 100%;
  min-width: 1100px;
  table-layout: auto;
}

.revenue-table th,
.revenue-table td {
  white-space: nowrap;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
